import { useEffect, useState } from "react";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// Local datetime formatted for an <input type="datetime-local"> (YYYY-MM-DDTHH:mm).
function toLocalInputValue(d: Date): string {
	const pad = (n: number) => String(n).padStart(2, "0");
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	taskTitle: string;
	defaultDurationMin: number;
	creating: boolean;
	// start is an ISO string; returns true if the event was created.
	onConfirm: (
		start: string,
		durationMin: number,
		title: string,
	) => Promise<boolean>;
}

export function AddToCalendarDialog({
	open,
	onOpenChange,
	taskTitle,
	defaultDurationMin,
	creating,
	onConfirm,
}: Props) {
	const [title, setTitle] = useState("");
	const [start, setStart] = useState("");
	const [duration, setDuration] = useState(defaultDurationMin);

	// Reset fields each time the dialog opens.
	useEffect(() => {
		if (open) {
			setTitle(taskTitle);
			setStart(toLocalInputValue(new Date()));
			setDuration(defaultDurationMin > 0 ? defaultDurationMin : 30);
		}
	}, [open, taskTitle, defaultDurationMin]);

	const valid =
		!!title.trim() &&
		!!start &&
		!Number.isNaN(Date.parse(start)) &&
		Number.isInteger(duration) &&
		duration > 0;

	async function handleConfirm() {
		if (!valid) return;
		const iso = new Date(start).toISOString();
		const ok = await onConfirm(iso, duration, title.trim());
		if (ok) onOpenChange(false);
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<DialogTitle>Add to Google Calendar</DialogTitle>
					<DialogDescription>
						Create a calendar event for this task.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 py-2">
					<div className="space-y-1.5">
						<label
							htmlFor="gcal-title"
							className="text-sm font-medium text-foreground"
						>
							Title
						</label>
						<Input
							id="gcal-title"
							type="text"
							value={title}
							onChange={(e) => setTitle(e.target.value)}
						/>
					</div>
					<div className="space-y-1.5">
						<label
							htmlFor="gcal-start"
							className="text-sm font-medium text-foreground"
						>
							Start
						</label>
						<Input
							id="gcal-start"
							type="datetime-local"
							value={start}
							onChange={(e) => setStart(e.target.value)}
						/>
					</div>
					<div className="space-y-1.5">
						<label
							htmlFor="gcal-duration"
							className="text-sm font-medium text-foreground"
						>
							Duration (minutes)
						</label>
						<Input
							id="gcal-duration"
							type="number"
							min={1}
							value={duration}
							onChange={(e) =>
								setDuration(Number(e.target.value))
							}
						/>
					</div>
				</div>

				<DialogFooter>
					<Button
						variant="ghost"
						onClick={() => onOpenChange(false)}
						disabled={creating}
					>
						Cancel
					</Button>
					<Button onClick={handleConfirm} disabled={!valid || creating}>
						{creating ? "Adding..." : "Add to Calendar"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
